import type {
  Block,
  Column,
  Design,
  MergeFieldInfo,
  SocialBlock,
  TextStyle,
} from '../lib/types.js';
import { Check, Field, Pill } from './ui.js';
import {
  findBlock,
  normaliseColumns,
  replaceBlock,
  setColumnCount,
  updateColumn,
} from '../lib/design.js';

/**
 * Property editor for the selected block, plus the brand and canvas settings.
 *
 * Form-based rather than direct manipulation: the values that actually matter
 * for email are numeric and exact (pixel widths, hex colours, whether a field
 * hides when empty), and typing 140 is more reliable than dragging to it.
 */
export function Inspector({
  design,
  selectedId,
  fields,
  readOnly,
  onChange,
}: {
  design: Design;
  selectedId: string | null;
  fields: MergeFieldInfo[];
  readOnly: boolean;
  onChange: (next: Design) => void;
}): JSX.Element {
  const entry = selectedId ? findBlock(design, selectedId) : null;

  return (
    <div className="col">
      <div className="card">
        <div className="card-head">
          <h2>{entry ? `${entry.block.type} block` : 'Canvas & brand'}</h2>
          {readOnly && <Pill tone="outline">read-only</Pill>}
        </div>
        <div className="card-body">
          {/*
            Stated here as well as in the page banner, because this is where
            somebody is when they try to type. A disabled field that gives no
            reason reads as a broken app rather than a locked one.
          */}
          {readOnly && (
            <p className="hint" style={{ marginTop: 0 }}>
              This version is published, so it cannot be edited. Restore it as a new draft from
              the Versions tab to make changes.
            </p>
          )}
          {entry ? (
            <BlockInspector
              design={design}
              block={entry.block}
              fields={fields}
              readOnly={readOnly}
              onChange={onChange}
            />
          ) : (
            <BrandInspector design={design} readOnly={readOnly} onChange={onChange} />
          )}
        </div>
      </div>

      {entry?.parent && (
        <ColumnInspector
          design={design}
          columnId={entry.parent.columnId}
          readOnly={readOnly}
          onChange={onChange}
        />
      )}
    </div>
  );
}

// --- Canvas and brand ------------------------------------------------------

function BrandInspector({
  design,
  readOnly,
  onChange,
}: {
  design: Design;
  readOnly: boolean;
  onChange: (next: Design) => void;
}): JSX.Element {
  const brand = design.brand;
  const setBrand = (patch: Partial<Design['brand']>) =>
    onChange({ ...design, brand: { ...brand, ...patch } });

  return (
    <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
      <Field
        label="Canvas width (px)"
        hint="600px is the safe ceiling. Outlook desktop ignores max-width, so this is a hard number."
      >
        <input
          type="number"
          min={200}
          max={900}
          value={design.maxWidth}
          onChange={(e) => onChange({ ...design, maxWidth: Number(e.target.value) || 600 })}
        />
      </Field>

      <Field
        label="Font"
        hint="Email clients do not load web fonts. Anything outside the email-safe list silently falls back."
      >
        <select value={brand.fontFamily} onChange={(e) => setBrand({ fontFamily: e.target.value })}>
          {[
            'Segoe UI',
            'Arial',
            'Helvetica',
            'Verdana',
            'Tahoma',
            'Trebuchet MS',
            'Georgia',
            'Times New Roman',
            'Calibri',
            'Courier New',
          ].map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Fallback stack">
        <input value={brand.fontFallback} onChange={(e) => setBrand({ fontFallback: e.target.value })} />
      </Field>

      <div className="field-row">
        <ColourField label="Text" value={brand.textColor} onChange={(v) => setBrand({ textColor: v })} />
        <ColourField label="Muted" value={brand.mutedColor} onChange={(v) => setBrand({ mutedColor: v })} />
      </div>
      <div className="field-row">
        <ColourField label="Accent" value={brand.accentColor} onChange={(v) => setBrand({ accentColor: v })} />
        <ColourField label="Links" value={brand.linkColor} onChange={(v) => setBrand({ linkColor: v })} />
      </div>
      <div className="field-row">
        <ColourField
          label="Dividers"
          value={brand.dividerColor}
          onChange={(v) => setBrand({ dividerColor: v })}
        />
        <ColourField
          label="Reference background"
          value={brand.backgroundColor}
          onChange={(v) => setBrand({ backgroundColor: v })}
        />
      </div>
      <p className="hint">
        The reference background is only used for contrast checking. Signatures are composed on top of
        the client&apos;s own background, so it is never painted.
      </p>
    </fieldset>
  );
}

function ColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <span className="row">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 38, flex: 'none' }}
          aria-label={`${label} colour picker`}
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mono"
          aria-label={`${label} hex value`}
        />
      </span>
    </label>
  );
}

// --- Columns ---------------------------------------------------------------

function ColumnInspector({
  design,
  columnId,
  readOnly,
  onChange,
}: {
  design: Design;
  columnId: string;
  readOnly: boolean;
  onChange: (next: Design) => void;
}): JSX.Element | null {
  let column: Column | null = null;
  let rowId = '';
  let total = 0;

  for (const block of design.blocks) {
    if (block.type !== 'row') continue;
    const match = block.columns.find((c) => c.id === columnId);
    if (match) {
      column = match;
      rowId = block.id;
      total = block.columns.reduce((a, c) => a + c.width, 0);
    }
  }
  if (!column) return null;

  const px = Math.round((column.width / (total || 100)) * design.maxWidth);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Containing column</h2>
        <span className="tiny muted">{px}px</span>
      </div>
      <div className="card-body">
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
          <Field label={`Width (${column.width}% of row)`}>
            <input
              type="range"
              min={5}
              max={95}
              value={column.width}
              onChange={(e) => onChange(updateColumn(design, columnId, { width: Number(e.target.value) }))}
            />
          </Field>
          <div className="field-row">
            <Field label="Vertical align">
              <select
                value={column.valign}
                onChange={(e) =>
                  onChange(updateColumn(design, columnId, { valign: e.target.value as Column['valign'] }))
                }
              >
                <option value="top">Top</option>
                <option value="middle">Middle</option>
                <option value="bottom">Bottom</option>
              </select>
            </Field>
            <Field label="Right padding">
              <input
                type="number"
                min={0}
                max={48}
                value={column.paddingRight}
                onChange={(e) =>
                  onChange(updateColumn(design, columnId, { paddingRight: Number(e.target.value) || 0 }))
                }
              />
            </Field>
          </div>
          {Math.abs(total - 100) > 1 && (
            <button className="sm" onClick={() => onChange(normaliseColumns(design, rowId))}>
              Rebalance to 100% (currently {total}%)
            </button>
          )}
        </fieldset>
      </div>
    </div>
  );
}

// --- Per-block editors -----------------------------------------------------

function BlockInspector({
  design,
  block,
  fields,
  readOnly,
  onChange,
}: {
  design: Design;
  block: Block;
  fields: MergeFieldInfo[];
  readOnly: boolean;
  onChange: (next: Design) => void;
}): JSX.Element {
  const set = (next: Block) => onChange(replaceBlock(design, block.id, next));

  return (
    <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
      {block.type === 'text' && (
        <>
          <Field
            label="Content"
            hint="Merge fields like {{JobTitle}} and links written as [text](https://url) are supported. Everything else is escaped."
          >
            <textarea
              value={block.content}
              rows={3}
              onChange={(e) => set({ ...block, content: e.target.value })}
            />
          </Field>
          <FieldTokenHelper
            fields={fields}
            onInsert={(token) => set({ ...block, content: `${block.content}${token}` })}
          />
          <Check
            label="Hide this line when every merge field in it is empty"
            checked={block.hideIfEmpty}
            onChange={(v) => set({ ...block, hideIfEmpty: v })}
          />
          <TextStyleEditor style={block.style} onChange={(style) => set({ ...block, style })} />
        </>
      )}

      {block.type === 'field' && (
        <>
          <Field label="Attribute">
            <select
              value={block.field}
              onChange={(e) => set({ ...block, field: e.target.value as typeof block.field })}
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.key} ({f.graphProperty}){f.oftenEmpty ? ' — often empty' : ''}
                </option>
              ))}
            </select>
          </Field>
          <div className="field-row">
            <Field label="Prefix">
              <input value={block.prefix} onChange={(e) => set({ ...block, prefix: e.target.value })} />
            </Field>
            <Field label="Suffix">
              <input value={block.suffix} onChange={(e) => set({ ...block, suffix: e.target.value })} />
            </Field>
          </div>
          <Field
            label="Fallback when empty"
            hint="Used instead of hiding the block. Leave blank and tick the box below to hide it instead."
          >
            <input value={block.fallback} onChange={(e) => set({ ...block, fallback: e.target.value })} />
          </Field>
          <Check
            label="Hide when the attribute is empty"
            checked={block.hideIfEmpty}
            onChange={(v) => set({ ...block, hideIfEmpty: v })}
            hint="Without this, a missing mobile number renders as a lone prefix."
          />
          <Field label="Link">
            <select
              value={block.link}
              onChange={(e) => set({ ...block, link: e.target.value as typeof block.link })}
            >
              <option value="none">None</option>
              <option value="tel">Phone (tel:)</option>
              <option value="mailto">Email (mailto:)</option>
            </select>
          </Field>
          <TextStyleEditor style={block.style} onChange={(style) => set({ ...block, style })} />
        </>
      )}

      {block.type === 'image' && (
        <>
          <Field label="Purpose">
            <select
              value={block.kind}
              onChange={(e) => set({ ...block, kind: e.target.value as typeof block.kind })}
            >
              <option value="logo">Logo</option>
              <option value="banner">Banner / promo</option>
              <option value="custom">Other</option>
            </select>
          </Field>
          <Field
            label="Image URL"
            hint="Must be an anonymously reachable https URL. A SharePoint link that needs sign-in renders as a broken image for external recipients."
          >
            <input value={block.src} onChange={(e) => set({ ...block, src: e.target.value })} />
          </Field>
          <Field
            label="Alt text"
            hint="Shown before images are downloaded, which is the default for external recipients."
          >
            <input value={block.alt} onChange={(e) => set({ ...block, alt: e.target.value })} />
          </Field>
          <div className="field-row">
            <Field label="Width (px)">
              <input
                type="number"
                min={1}
                max={design.maxWidth}
                value={block.width}
                onChange={(e) => set({ ...block, width: Number(e.target.value) || 1 })}
              />
            </Field>
            <Field label="Height (px)">
              <input
                type="number"
                min={1}
                value={block.height ?? ''}
                placeholder="auto"
                onChange={(e) =>
                  set({
                    ...block,
                    ...(e.target.value ? { height: Number(e.target.value) } : { height: undefined }),
                  })
                }
              />
            </Field>
          </div>
          <Field label="Link to (optional)">
            <input
              value={block.href ?? ''}
              placeholder="https://example.com"
              onChange={(e) => set({ ...block, ...(e.target.value ? { href: e.target.value } : { href: undefined }) })}
            />
          </Field>
          <AlignField
            value={block.align}
            onChange={(align) => set({ ...block, align })}
          />
          <SpacingFields
            top={block.marginTop}
            bottom={block.marginBottom}
            onChange={(top, bottom) => set({ ...block, marginTop: top, marginBottom: bottom })}
          />
        </>
      )}

      {block.type === 'social' && <SocialEditor block={block} onChange={set} />}

      {block.type === 'divider' && (
        <>
          <ColourField label="Colour" value={block.color} onChange={(color) => set({ ...block, color })} />
          <div className="field-row">
            <Field label="Thickness (px)">
              <input
                type="number"
                min={1}
                max={8}
                value={block.thickness}
                onChange={(e) => set({ ...block, thickness: Number(e.target.value) || 1 })}
              />
            </Field>
            <Field label="Width (%)">
              <input
                type="number"
                min={10}
                max={100}
                value={block.width}
                onChange={(e) => set({ ...block, width: Number(e.target.value) || 100 })}
              />
            </Field>
          </div>
          <Field label="Space above and below (px)">
            <input
              type="number"
              min={0}
              max={48}
              value={block.spacing}
              onChange={(e) => set({ ...block, spacing: Number(e.target.value) || 0 })}
            />
          </Field>
        </>
      )}

      {block.type === 'spacer' && (
        <Field label="Height (px)">
          <input
            type="number"
            min={1}
            max={96}
            value={block.height}
            onChange={(e) => set({ ...block, height: Number(e.target.value) || 1 })}
          />
        </Field>
      )}

      {block.type === 'disclaimer' && (
        <>
          <Field label="Disclaimer text" hint="Links written as [text](https://url) are supported.">
            <textarea
              rows={6}
              value={block.content}
              onChange={(e) => set({ ...block, content: e.target.value })}
            />
          </Field>
          <TextStyleEditor style={block.style} onChange={(style) => set({ ...block, style })} />
        </>
      )}

      {block.type === 'row' && (
        <>
          <Field label="Number of columns">
            <select
              value={block.columns.length}
              onChange={(e) => onChange(setColumnCount(design, block.id, Number(e.target.value)))}
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
          <Check
            label="Stack columns on narrow screens"
            checked={block.stackOnMobile}
            onChange={(v) => set({ ...block, stackOnMobile: v })}
            hint="Honoured by OWA, Outlook mobile and Apple Mail. Outlook desktop and the Gmail app ignore it and keep columns side by side."
          />
          <p className="hint">
            Widths: {block.columns.map((c) => `${c.width}%`).join(' / ')} (
            {block.columns.reduce((a, c) => a + c.width, 0)}% total). Select a block inside a column to
            adjust that column.
          </p>
        </>
      )}
    </fieldset>
  );
}

function SocialEditor({
  block,
  onChange,
}: {
  block: SocialBlock;
  onChange: (next: Block) => void;
}): JSX.Element {
  const setIcon = (index: number, patch: Partial<SocialBlock['icons'][number]>) => {
    onChange({
      ...block,
      icons: block.icons.map((icon, i) => (i === index ? { ...icon, ...patch } : icon)),
    });
  };

  return (
    <>
      <div className="field-row">
        <Field label="Icon size (px)">
          <input
            type="number"
            min={12}
            max={64}
            value={block.size}
            onChange={(e) => onChange({ ...block, size: Number(e.target.value) || 24 })}
          />
        </Field>
        <Field label="Gap (px)">
          <input
            type="number"
            min={0}
            max={32}
            value={block.gap}
            onChange={(e) => onChange({ ...block, gap: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <AlignField value={block.align} onChange={(align) => onChange({ ...block, align })} />

      {block.icons.map((icon, i) => (
        <div key={i} className="card" style={{ padding: 10, marginBottom: 8 }}>
          <div className="row mb">
            <select
              value={icon.network}
              onChange={(e) => setIcon(i, { network: e.target.value as typeof icon.network })}
            >
              {['linkedin', 'x', 'facebook', 'instagram', 'youtube', 'github', 'custom'].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="spacer" />
            <button
              className="ghost icon"
              aria-label={`Remove ${icon.network}`}
              onClick={() => onChange({ ...block, icons: block.icons.filter((_, j) => j !== i) })}
            >
              ×
            </button>
          </div>
          <Field label="Profile URL">
            <input value={icon.url} onChange={(e) => setIcon(i, { url: e.target.value })} />
          </Field>
          <Field label="Icon image URL" hint="PNG, not SVG. Outlook and Gmail do not render SVG.">
            <input value={icon.src} onChange={(e) => setIcon(i, { src: e.target.value })} />
          </Field>
          <Field label="Alt text">
            <input value={icon.alt} onChange={(e) => setIcon(i, { alt: e.target.value })} />
          </Field>
        </div>
      ))}

      {block.icons.length < 8 && (
        <button
          className="sm"
          onClick={() =>
            onChange({
              ...block,
              icons: [
                ...block.icons,
                {
                  network: 'custom',
                  url: 'https://example.com',
                  src: 'https://placehold.co/48x48/666666/ffffff.png',
                  alt: '',
                },
              ],
            })
          }
        >
          + Add icon
        </button>
      )}
    </>
  );
}

function TextStyleEditor({
  style,
  onChange,
}: {
  style: TextStyle;
  onChange: (style: TextStyle) => void;
}): JSX.Element {
  const set = (patch: Partial<TextStyle>) => onChange({ ...style, ...patch });

  return (
    <>
      <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '12px 0' }} />
      <div className="field-row">
        <Field label="Size (px)">
          <input
            type="number"
            min={6}
            max={48}
            value={style.fontSize}
            onChange={(e) => set({ fontSize: Number(e.target.value) || 13 })}
          />
        </Field>
        <Field label="Line height">
          <input
            type="number"
            step={0.05}
            min={0.8}
            max={3}
            value={style.lineHeight}
            onChange={(e) => set({ lineHeight: Number(e.target.value) || 1.4 })}
          />
        </Field>
      </div>
      <ColourField label="Colour" value={style.color} onChange={(color) => set({ color })} />
      <div className="row wrap mb">
        <button className={style.bold ? 'sm primary' : 'sm'} onClick={() => set({ bold: !style.bold })}>
          Bold
        </button>
        <button
          className={style.italic ? 'sm primary' : 'sm'}
          onClick={() => set({ italic: !style.italic })}
        >
          Italic
        </button>
      </div>
      <AlignField value={style.align} onChange={(align) => set({ align })} />
      <div className="field-row">
        <Field label="Letter spacing (px)" hint="Dropped by Outlook desktop.">
          <input
            type="number"
            min={-1}
            max={6}
            value={style.letterSpacing}
            onChange={(e) => set({ letterSpacing: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Case">
          <select
            value={style.transform}
            onChange={(e) => set({ transform: e.target.value as TextStyle['transform'] })}
          >
            <option value="none">As typed</option>
            <option value="uppercase">UPPERCASE</option>
            <option value="capitalize">Capitalised</option>
          </select>
        </Field>
      </div>
      <SpacingFields
        top={style.marginTop}
        bottom={style.marginBottom}
        onChange={(marginTop, marginBottom) => set({ marginTop, marginBottom })}
      />
    </>
  );
}

function AlignField({
  value,
  onChange,
}: {
  value: 'left' | 'center' | 'right';
  onChange: (v: 'left' | 'center' | 'right') => void;
}): JSX.Element {
  return (
    <div className="field">
      <span className="field-label">Alignment</span>
      <div className="seg">
        {(['left', 'center', 'right'] as const).map((a) => (
          <button key={a} type="button" className={value === a ? 'on' : ''} onClick={() => onChange(a)}>
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}

function SpacingFields({
  top,
  bottom,
  onChange,
}: {
  top: number;
  bottom: number;
  onChange: (top: number, bottom: number) => void;
}): JSX.Element {
  return (
    <div className="field-row">
      <Field label="Space above (px)">
        <input
          type="number"
          min={0}
          max={64}
          value={top}
          onChange={(e) => onChange(Number(e.target.value) || 0, bottom)}
        />
      </Field>
      <Field label="Space below (px)">
        <input
          type="number"
          min={0}
          max={64}
          value={bottom}
          onChange={(e) => onChange(top, Number(e.target.value) || 0)}
        />
      </Field>
    </div>
  );
}

function FieldTokenHelper({
  fields,
  onInsert,
}: {
  fields: MergeFieldInfo[];
  onInsert: (token: string) => void;
}): JSX.Element {
  return (
    <div className="field">
      <span className="field-label">Insert a merge field</span>
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) onInsert(e.target.value);
        }}
      >
        <option value="">Choose a field…</option>
        {fields.map((f) => (
          <option key={f.key} value={f.token}>
            {f.key}
            {f.oftenEmpty ? ' (often empty)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
